import { AlertCircle, MoreVerticalIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useQueryClient } from "@tanstack/react-query";

import { useDeleteRoomSystem } from "../api/delete-room-system";

type RoomSystemRowActionsProps = {
  roomSystemId: string;
};

export function RoomSystemRowActions(props: RoomSystemRowActionsProps) {
  const {
    mutateAsync: deleteRoomSystem,
    isPending,
    isSuccess,
    isError,
    error,
    reset,
  } = useDeleteRoomSystem();
  const [isDeleting, setIsDeleting] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isAdminView = useMatch("/admin/room-systems");

  useEffect(() => {
    if (!isDeleting) {
      reset();
    }
  }, [isDeleting, reset]);

  const handleDelete = async () => {
    try {
      await deleteRoomSystem({ id: props.roomSystemId });
      queryClient.invalidateQueries({ queryKey: ["room-systems", "admin"] });
      setIsDeleting(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAdminEdit = () => {
    navigate(`/admin/room-systems/${props.roomSystemId}`);
  };

  const handleSessionEdit = () => {
    navigate(`/sessions/room-systems/${props.roomSystemId}`);
  };

  return (
    <Dialog onOpenChange={setIsDeleting} open={isDeleting}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={isAdminView ? handleAdminEdit : handleSessionEdit}
          >
            {isAdminView ? "Edit" : "View"}
          </DropdownMenuItem>

          {isAdminView && (
            <DialogTrigger asChild>
              <DropdownMenuItem className="text-destructive">
                Delete
              </DropdownMenuItem>
            </DialogTrigger>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
          <DialogDescription>
            Are you sure you would like to remove this room system?
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-foreground">
            Removing this room system will remove it from sessions.
          </p>
        </DialogBody>
        {isError && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending || isSuccess}
            isLoading={isPending || isSuccess}
            isAnimated
          >
            Delete Room System
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
